// Post-approval "what to do next" CTA on the Quote detail page.
//
// CEO 2026-07-24: "we don't need steps in the SO on 'what's next' — it wasn't
// intuitive; make it very clear what the next steps are." This REPLACES the old
// four-step ProductionNextSteps progress strip (a horizontal rail that made the
// rep read four columns and infer which to touch) with ONE prominent, state-
// aware primary action. The pure derivation (deriveNextAction) picks the single
// action; this component renders it as either a big primary button (the create
// step) or a same-tab deep link into the target PO (send / add-tracking), plus
// a quiet one-liner for the terminal/blocked states.
//
// Data source is the SAME endpoint the project POs tab uses
// (/projects/:id/purchase-orders) — no new API. Tracking presence isn't in that
// payload, so delivery progress is read from each PO's stage and reps are deep-
// linked to the PO page to add it (see nextAction.ts).
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Package, Send, Truck } from "lucide-react";
import type { PoPrecondition } from "@merchd/shared";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { deriveNextAction } from "@/components/orders/nextAction";

interface ProjectPoRow {
  id: string;
  poNumber: number;
  orderId: string;
  internalStage: string;
  sentAt: string | null;
  vendorId: string;
  vendorName: string;
  poType: string;
  shipFromVendorId: string | null;
}

interface PosListResponse {
  rows: ProjectPoRow[];
}

// Reuse the ProjectPosTab query key so the two surfaces share one cache entry.
function posQueryKey(projectId: string) {
  return ["project", projectId, "purchase-orders"] as const;
}

interface NextActionCtaProps {
  orderId: string;
  projectId: string;
  hasProductLines: boolean;
  /** Admin / production_rep — only they can create POs. The view states
   *  (send / add tracking) are links any staff may follow. */
  canCreatePo: boolean;
  onCreatePo: () => void;
  /** Fire the create with the missing_artwork override (skip_artwork_check) —
   *  the "Create anyway" escape hatch on the one overridable prereq. */
  onCreateAnyway: () => void;
  createPoPending: boolean;
  /**
   * Client-side mirror of the server's PO-creation prereqs (derivePoPreconditions
   * in @merchd/shared), computed in OrderDetailPage from data it already holds.
   * When not `ok`, the create button is DISABLED with the plain `message` — the
   * rep sees WHY (no address / vendor missing / N need artwork) instead of a
   * click → 409 → guess ping-pong. Only used in the create_pos state.
   */
  precondition: PoPrecondition;
  /**
   * Compact single-line ship-to summary (e.g. "Acme Co · Austin, TX") derived
   * from the order's shipping-address snapshot in OrderDetailPage. `null` when
   * the order has no shipping address yet — the create step then points the rep
   * to the Addresses panel via the #order-addresses anchor.
   */
  shipsTo: string | null;
  /** Prefix for the quote's in-page anchors (#order-addresses, #order-line-…)
   *  when the CTA renders OFF the quote page (the project's Shipping tab). */
  anchorBase?: string;
  /** On the quote page: purchase orders are created on the Shipping tab once
   *  every item's shipping is filled in (Stephanie 2026-10-13), so the create
   *  step becomes a pointer there instead of the button. */
  createOnShippingHref?: string;
}

// Shared shell so every state reads as the same prominent action card. CEO
// 2026-07-24: "Remove the 'Next Step' from the SO — this has already been
// discussed." The label is gone; the single state-aware CTA speaks for itself.
function CtaCard({ children }: { children: React.ReactNode }) {
  return (
    <section className="mb-6 rounded-lg border border-brown-100 bg-white p-5">
      {children}
    </section>
  );
}

export function NextActionCta({
  orderId,
  projectId,
  hasProductLines,
  canCreatePo,
  onCreatePo,
  onCreateAnyway,
  createPoPending,
  precondition,
  shipsTo,
  anchorBase = "",
  createOnShippingHref,
}: NextActionCtaProps) {
  const { data, isLoading } = useQuery({
    queryKey: posQueryKey(projectId),
    queryFn: () =>
      api<PosListResponse>(`/projects/${projectId}/purchase-orders`),
    enabled: projectId.length > 0,
  });

  // This project's Quote owns these POs; filter defensively by orderId.
  const pos = (data?.rows ?? []).filter((r) => r.orderId === orderId);
  const action = deriveNextAction({ hasProductLines }, pos);

  if (isLoading) {
    return (
      <CtaCard>
        <p className="text-sm text-brown-300">Checking purchase orders…</p>
      </CtaCard>
    );
  }

  // No product lines — nothing to produce yet. Quiet guidance, no button.
  if (action.kind === "no_lines") {
    return (
      <CtaCard>
        <p className="text-sm text-brown-400">
          Add product lines to start production.
        </p>
      </CtaCard>
    );
  }

  // Create POs — the sales→production handoff. Role-gated: only a creator sees
  // the button; everyone else sees the quiet who-does-this line.
  if (action.kind === "create_pos" && createOnShippingHref) {
    return (
      <CtaCard>
        <p className="text-sm text-brown-400">
          Purchase orders are created on the{" "}
          <Link to={createOnShippingHref} className="font-semibold text-ink hover:text-muted">
            Shipping tab →
          </Link>{" "}
          once every item's shipping is filled in.
        </p>
      </CtaCard>
    );
  }
  if (action.kind === "create_pos") {
    const cancelledCopy =
      action.cancelledCount > 0
        ? `${action.cancelledCount} purchase ${
            action.cancelledCount === 1 ? "order was" : "orders were"
          } cancelled — create a new one. `
        : "";
    if (!canCreatePo) {
      return (
        <CtaCard>
          <p className="text-sm text-brown-400">
            {action.cancelledCount > 0
              ? "The purchase orders were cancelled — a production rep or admin creates a new one."
              : "A production rep or admin creates the purchase orders."}
          </p>
        </CtaCard>
      );
    }
    // Right-sized (2026-07-26): a COMPACT primary consistent with the page's
    // action grammar (Button size="sm"), not the old h-12 full-bleed hero that
    // dominated the top of a committed SO. Disabled-with-honest-reason mirrors
    // the server prereqs from data the page already holds, so the rep sees WHY
    // before any round-trip.
    const blocked = !precondition.ok;
    return (
      <CtaCard>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onCreatePo}
            disabled={createPoPending || blocked}
          >
            <Package className="h-4 w-4" aria-hidden />
            {createPoPending ? "Creating…" : "Create purchase orders"}
          </Button>
          {/* missing_artwork is the ONE overridable prereq (blanks / samples
              legitimately ship without artwork) — keep the "Create anyway"
              escape hatch inline so the rep proceeds without a round-trip. */}
          {blocked && precondition.overridable && (
            <button
              type="button"
              onClick={onCreateAnyway}
              disabled={createPoPending}
              className="text-sm font-semibold text-ink underline-offset-2 hover:text-muted hover:underline disabled:opacity-50"
            >
              Create anyway
            </button>
          )}
        </div>
        {blocked ? (
          // Plain, honest reason mirrored from the server prereq that would
          // reject this create — no address / vendor missing / N need artwork.
          <p className="mt-3 text-sm text-brown-400">
            {precondition.message}
            {precondition.reason === "no_shipping_address" && (
              <>
                {" "}
                <a
                  href={`${anchorBase}#order-addresses`}
                  className="font-semibold text-ink hover:text-muted"
                >
                  Set it in Addresses
                </a>
                .
              </>
            )}
            {/* vendor_missing: deep-link straight to the offending line's edit
                action (round-35 editor) — the concrete remedy is re-selecting
                the product on that line, which re-snapshots the supplier. */}
            {precondition.reason === "vendor_missing" &&
              precondition.offendingLineId && (
                <>
                  {" "}
                  <a
                    href={`${anchorBase}#order-line-${precondition.offendingLineId}`}
                    className="font-semibold text-ink hover:text-muted"
                  >
                    Go to the item →
                  </a>
                </>
              )}
            {/* missing_artwork: the message above is the server's verbatim
                per-location detail; deep-link each named (item, location) pair
                to its offending line via the #order-line-{id} anchor. */}
            {precondition.reason === "missing_artwork" &&
              precondition.missingArtwork.map((m, i) => (
                <span key={`${m.lineItemId}-${m.location}-${i}`}>
                  {" "}
                  <a
                    href={`${anchorBase}#order-line-${m.lineItemId}`}
                    className="font-semibold text-ink hover:text-muted"
                  >
                    Fix {m.itemName} — {m.location} →
                  </a>
                </span>
              ))}
          </p>
        ) : /* Ship-to confirmation: the SO's shipping address auto-carries to
              each PO, so reps see WHERE this order ships before they mint POs.
              Graceful absence points them to the Addresses panel. */
        shipsTo ? (
          <p className="mt-3 text-sm text-brown-400">
            {cancelledCopy}
            <span className="font-semibold text-brown-500">Ships to:</span>{" "}
            {shipsTo}
          </p>
        ) : (
          <p className="mt-3 text-sm text-brown-400">
            {cancelledCopy}No shipping address on this order yet —{" "}
            <a
              href={`${anchorBase}#order-addresses`}
              className="font-semibold text-ink hover:text-muted"
            >
              set it in Addresses
            </a>
            .
          </p>
        )}
      </CtaCard>
    );
  }

  // Send POs — review & send the first unsent PO. Same-tab deep link; any staff.
  // Kept as the prominent action (ux review 2026-07-26): send/add-tracking are
  // never-gated, always-actionable single next actions — the hero sizing is
  // correct here (only the GATED create step was downsized).
  if (action.kind === "send_pos") {
    return (
      <CtaCard>
        <Link
          to={`/projects/${projectId}/pos/${action.poId}`}
          className="inline-flex h-12 items-center gap-2 rounded-lg bg-ink px-6 text-base font-semibold text-white hover:bg-ink-700"
        >
          <Send className="h-5 w-5" aria-hidden />
          Review &amp; send PO #{action.poNumber}
        </Link>
        <p className="mt-3 text-sm text-brown-400">
          {action.sentCount} of {action.totalCount} POs sent.
        </p>
      </CtaCard>
    );
  }

  // Add tracking — first PO without tracking. Same-tab deep link; any staff.
  // Kept prominent (ux review 2026-07-26) — always-actionable, correctly a hero.
  if (action.kind === "add_tracking") {
    return (
      <CtaCard>
        <Link
          to={`/projects/${projectId}/pos/${action.poId}`}
          className="inline-flex h-12 items-center gap-2 rounded-lg bg-ink px-6 text-base font-semibold text-white hover:bg-ink-700"
        >
          <Truck className="h-5 w-5" aria-hidden />
          Add tracking on PO #{action.poNumber}
        </Link>
        <p className="mt-3 text-sm text-brown-400">
          Tracking: {action.trackedCount} of {action.totalCount}.
        </p>
      </CtaCard>
    );
  }

  // Complete — every active PO tracked/delivered. Quiet terminal line, no button.
  return (
    <CtaCard>
      <p className="text-sm font-semibold text-brown-500">
        Production complete — {action.totalCount} purchase{" "}
        {action.totalCount === 1 ? "order" : "orders"} delivered.
      </p>
    </CtaCard>
  );
}
